// src/components/layout/Footer.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';
import { FileText, Shield, Cookie, Mail, Heart } from 'lucide-react';
import SocialLinks from './SocialLinks';

const Footer = () => {
  const { t } = useTranslation();
  // Footer links are rendered as real <a href> (react-router <Link>), not
  // buttons. They previously used <button onClick={navigate}>, which works for
  // people but is invisible to crawlers: About/Contact/FAQ/Blog/Terms/Privacy
  // had no inbound internal link anywhere on the site and passed no link equity.
  const lang = useLangParam();

  const legalLinks = [
    { path: '/terms', label: t('footer.terms'), icon: <FileText className="w-4 h-4" /> },
    { path: '/privacy', label: t('footer.privacy'), icon: <Shield className="w-4 h-4" /> },
    { path: '/cookies', label: t('footer.cookies'), icon: <Cookie className="w-4 h-4" /> },
  ];

  const quickLinks = [
    { path: '/about', label: t('footer.about') },
    { path: '/contact', label: t('footer.contact') },
    { path: '/faq', label: t('footer.faq') },
    { path: '/blog', label: t('footer.blog') },
    // Sitewide entry point to the public exam pages so they are reachable by
    // internal link, not only from sitemap.xml.
    { path: '/mock-exams', label: t('footer.mockExams', { defaultValue: 'Mock exams' }) },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="col-span-1">
            <h3 className="text-white text-2xl font-bold mb-4">Logami</h3>
            <p className="text-gray-400 text-sm mb-4">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span>{t('footer.madeWith')}</span>
              <Heart className="w-4 h-4 text-red-500 fill-current" />
              <span>{t('footer.inGermany')}</span>
            </div>
            <SocialLinks
              className="mt-5"
              buttonClassName="border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"
            />
          </div>

          {/* Quick Links */}
          <div className="col-span-1">
            <h4 className="text-white font-semibold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={`/${lang}${link.path}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div className="col-span-1">
            <h4 className="text-white font-semibold mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={`/${lang}${link.path}`}
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.icon}
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Section */}
          <div className="col-span-1">
            <h4 className="text-white font-semibold mb-4">{t('footer.contactUs')}</h4>
            <div className="space-y-3">
              <a
                href="mailto:team@logami.app"
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                <span>team@logami.app</span>
              </a>
              <p className="text-gray-400 text-sm">
                {t('footer.location')}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm text-center md:text-left">
            © {new Date().getFullYear()} Logami. {t('footer.rights')}
          </p>

          <div className="flex flex-col items-center md:items-end gap-2 text-xs text-gray-500">
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {t('footer.gdprCompliant')}
            </span>
            <span className="text-center md:text-right">
              {t('footer.disclaimer')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;